// src/customReports/executive-learning-at-the-speed-of-change.js

import React from 'react';
import './executive-learning-at-the-speed-of-change.css';

const REPORT_SUBTITLE = 'How AI Is Reshaping Demand, Delivery, and the Learning Partnership';
const DOWNLOAD_ANCHOR = '#report-download';

const overviewItems = [
  {
    title: 'The demand-side reality',
    text: 'Organizations are moving beyond early AI experimentation, but many are still working out how to turn adoption into sustained enterprise value.'
  },
  {
    title: 'Pressure on current learning models',
    text: 'Leaders report that existing learning provision is often too slow, too generic, or too disconnected from real work to meet the pace of AI transformation.'
  },
  {
    title: 'A changing partnership model',
    text: 'The role of the learning partner is shifting from one-way knowledge transfer toward collaboration, contextual design, live application, and ongoing capability development.'
  }
];

const audienceCards = [
  {
    title: 'For learning leaders',
    points: [
      'Understand where AI capability gaps are emerging',
      'Benchmark current learning approaches against peer organizations',
      'Explore why adaptive, work-embedded learning is gaining importance',
      'Identify where learning can support trust, adoption, and strategic alignment'
    ]
  },
  {
    title: 'For business schools and learning providers',
    points: [
      'See how expectations of learning partners are changing',
      'Understand the demand for contextual, cross-functional learning',
      'Explore opportunities beyond technical AI training',
      'Consider how to support co-created, continuously refreshed learning models'
    ]
  },
  {
    title: 'For executive education teams',
    points: [
      'Clarify where executive education can add value in AI transformation',
      'Understand the growing emphasis on strategic and cognitive capabilities',
      'Explore how partnerships may need to become more iterative and applied',
      'Identify implications for curriculum design, delivery, and client engagement'
    ]
  }
];

const researchStats = [
  {
    value: '180',
    label: 'Senior leaders surveyed'
  },
  {
    value: '18',
    label: 'In-depth interviews with transformation leaders'
  },
  {
    value: '6',
    label: 'Markets included: USA, UK, France, Germany, Spain, Singapore'
  },
  {
    value: '2',
    label: 'AI maturity groups compared'
  }
];

const keyQuestions = [
  {
    question: 'How are organizations experiencing AI transformation today?',
    answer: 'AI adoption is accelerating, but many organizations are still working through how to translate experimentation and productivity gains into enterprise-level value. The report explores the gap between current AI use, often focused on efficiency, and future expectations around new ways of working, operating models, and value creation.'
  },
  {
    question: 'What is slowing progress?',
    answer: 'The research points to a broad challenge landscape rather than a single blocker. Leaders cite AI literacy gaps, strategy and organizational design, data readiness, governance, trust in AI outputs, and workforce resistance as interrelated constraints.'
  },
  {
    question: 'Are current learning models keeping pace?',
    answer: 'Many leaders report that existing learning provision struggles with the speed, specificity, and applied nature of AI transformation. The report examines why static, generic, or one-off approaches are under pressure.'
  },
  {
    question: 'What kinds of learning are organizations looking for?',
    answer: 'Demand is shifting toward learning that is continuous, contextual, cross-functional, and connected to live work. Hands-on experimentation is especially important, but many organizations lack protected time and safe environments for it.'
  },
  {
    question: 'How is the role of the learning partner changing?',
    answer: 'Organizations increasingly see learning partners as collaborators rather than simply expert content providers. The report explores the move toward co-creation, joint application, faster refresh cycles, and learning partnerships that evolve alongside AI transformation.'
  },
  {
    question: 'Where can business schools and universities add value?',
    answer: 'The research suggests a strategic opportunity around capabilities such as strategic thinking, critical thinking, adaptability, cross-functional collaboration, responsible AI, and governance. The opportunity is not to replace technical training, but to support the broader cognitive and organizational capabilities AI transformation requires.'
  }
];

export default function ExecutiveLearningSpeedReport() {
  return (
    <section className="speed-report">
      <section className="speed-report-intro">
        <h2>{REPORT_SUBTITLE}</h2>
        <p className="speed-report-hero-summary">
          A joint research initiative from Nexed Insight and UNICON exploring
          how organizations are rethinking executive learning, capability
          building, and learning partnerships in the AI era.
        </p>
        <p>
          Based on interviews with senior transformation leaders and a survey
          of 180 leaders across six global markets, the report examines a
          central tension: AI adoption is accelerating, but value realization
          remains uneven, and traditional learning models are under pressure
          to keep pace.
        </p>
        <a className="speed-report-cta" href={DOWNLOAD_ANCHOR}>
          Download The Full Report
        </a>
      </section>

      <section className="speed-report-section">
        <div className="speed-report-section-heading">
          <h2>What this report explores</h2>
          <p>
            The report maps how demand, delivery, and learning partnerships are
            changing as AI becomes a larger organizational priority.
          </p>
        </div>
        <div className="speed-report-overview-grid">
          {overviewItems.map((item) => (
            <article className="speed-report-overview-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="speed-report-section">
        <div className="speed-report-section-heading">
          <h2>Why this research matters</h2>
          <p>
            The findings are relevant for organizations building AI capability,
            and for the schools, universities, and providers supporting them.
          </p>
        </div>
        <div className="speed-report-audience-grid">
          {audienceCards.map((card) => (
            <article className="speed-report-audience-card" key={card.title}>
              <h3>{card.title}</h3>
              <ul>
                {card.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="speed-report-section speed-report-research-base">
        <div className="speed-report-section-heading">
          <h2>Research base</h2>
        </div>
        <div className="speed-report-stats">
          {researchStats.map((stat) => (
            <div className="speed-report-stat" key={stat.label}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
        <p className="speed-report-note">
          The study combines qualitative interviews with a quantitative survey
          to capture both lived experience and broader patterns in organizational
          demand.
        </p>
      </section>

      <section className="speed-report-section">
        <div className="speed-report-section-heading">
          <h2>Key questions this research answers</h2>
          <p>
            The report frames its findings around the tensions leaders are
            navigating as AI capability building becomes a strategic priority.
          </p>
        </div>
        <div className="speed-report-question-list">
          {keyQuestions.map((item, index) => (
            <article className="speed-report-question-card" key={item.question}>
              <span className="speed-report-question-number">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="speed-report-section speed-report-featured">
        <div className="speed-report-section-heading">
          <h2>Featured insight: AI maturity changes learning needs</h2>
          <p>
            AI maturity is used throughout the report as a lens for
            understanding how organizational needs shift as AI efforts develop.
            The findings suggest that more mature organizations do not simply
            need more advanced technical training; they place greater emphasis
            on strategic, cognitive, and cross-functional capabilities.
          </p>
        </div>
        <div className="speed-report-maturity-grid">
          <div>
            <h3>Lower-maturity organizations</h3>
            <p>
              Often place greater emphasis on foundational literacy, adoption,
              organizational design, and technical understanding.
            </p>
          </div>
          <div>
            <h3>Higher-maturity organizations</h3>
            <p>
              More often prioritize strategic thinking, critical thinking,
              collaboration across functions, live application, and sustained
              learning over time.
            </p>
          </div>
        </div>
        <p className="speed-report-closing-line">
          This points to a wider shift: AI capability building is becoming less
          about one-off training and more about adaptive learning systems that
          evolve with the work.
        </p>
      </section>

      <section className="speed-report-final-cta">
        <h2>Download the full report</h2>
        <p>
          Read the full report to explore the findings, data, and implications
          for learning leaders, business schools, universities, and executive
          education providers navigating AI transformation.
        </p>
        <a className="speed-report-cta" href={DOWNLOAD_ANCHOR}>
          Download The Full Report
        </a>
      </section>
    </section>
  );
}
